import {useRef, useMemo, useCallback} from 'react'
import {ChevronRightIcon, KebabHorizontalIcon, SkipIcon, XCircleFillIcon, CheckIcon} from '@primer/octicons-react'
import {IconButton, ActionMenu, ActionList} from '@primer/react'
import {useSessionContext} from '../../contexts/SessionContext'
import {
  isActiveSession,
  isPendingSession,
  isCancelledSession,
  isCompletedSession,
  isFailedSession,
} from '../../types/session'
import {generateWorkflowRunLink} from '../../utils/generate-actions-link'
import {generateTaskPath, generateTaskPathForRepo} from '../../utils/generate-task-path'
import styles from './SessionHeader.module.css'
import {clsx} from 'clsx'
import SessionElapsedTime from '../SessionElapsedTime'
import {copyText} from '@github-ui/copy-to-clipboard'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useIntentDetection} from '../../hooks/useIntentDetection'
import {extractAgentInstructions} from '../../utils/extract-agent-instructions'
import {AgentAvatar} from '../AgentAvatar'

export function SessionHeader({
  nwo,
  isCollapsed,
  onModifierClick,
  contentId,
  onOpenChange,
  shouldHighlight = false,
}: {
  nwo: string | undefined
  isCollapsed: boolean
  onModifierClick: (expand: boolean) => void
  contentId?: string
  onOpenChange: (open: boolean) => void
  shouldHighlight?: boolean
}) {
  const {session, logs: contextLogs} = useSessionContext()
  const workflowLink = generateWorkflowRunLink(nwo ?? null, session.workflow_run_id)
  const {isChatSession, isUsingIntentDetection} = useIntentDetection(session)

  const agentInstructions = useMemo(
    () => extractAgentInstructions(contextLogs ?? session.log_entries),
    [contextLogs, session.log_entries],
  )

  let sessionLink = generateTaskPath(session.resource_global_id, session.id)
  if (nwo) {
    const taskUrl = generateTaskPathForRepo({
      repositoryNwo: nwo,
      taskId: session.task_id,
      sessionId: session.id,
      isUsingIntentDetection,
    })
    if (taskUrl) {
      sessionLink = taskUrl
    }
  }

  const showMoreActionsMenu = !!(workflowLink || sessionLink || agentInstructions)

  const ref = useRef<HTMLDivElement>(null)

  const isActive = isActiveSession(session)
  const isPending = isPendingSession(session)
  const isCompleted = isCompletedSession(session)
  const isCancelled = isCancelledSession(session)
  const isFailed = isFailedSession(session)

  const handleClick = (event: React.MouseEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if ((event.altKey || event.metaKey) && onModifierClick) {
      event.preventDefault()
      event.stopPropagation()
      // If currently collapsed, expand all; if expanded, collapse all
      onModifierClick(!!isCollapsed)

      sendEvent('agent_sessions.task_view', {
        target: 'SESSION_HEADER_MODIFIER',
        action: isCollapsed ? 'EXPAND_ALL' : 'COLLAPSE_ALL',
        session_id: session.id,
        task_id: session.resource_global_id,
      })

      return false // Prevent further event handling
    } else {
      sendEvent('agent_sessions.task_view', {
        target: 'SESSION_HEADER',
        action: isCollapsed ? 'EXPAND' : 'COLLAPSE',
        session_id: session.id,
        task_id: session.resource_global_id,
      })
    }

    onOpenChange(isCollapsed)
  }

  const handleLinkClick = (e: React.MouseEvent) => {
    sendEvent('agent_sessions.task_view', {
      target: 'CLICK_WORKFLOW_LOGS_LINK',
      session_id: session.id,
      task_id: session.resource_global_id,
    })
    // Allow cmd/ctrl + click to open in new tab
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.metaKey || e.ctrlKey) {
      e.stopPropagation()
      return
    }
  }

  const handleCopyLink = useCallback(
    async (event: React.SyntheticEvent) => {
      if (sessionLink) {
        try {
          const fullUrl = window.location.origin + sessionLink
          await copyText(fullUrl)
          sendEvent('agent_sessions.task_view', {
            target: 'COPY_SESSION_LINK',
            session_id: session.id,
            task_id: session.resource_global_id,
          })
        } catch {
          // prevent menu from closing on error
          event.preventDefault()
          event.stopPropagation()
        }
      }
    },
    [sessionLink, session],
  )

  const handleCopyPrompt = useCallback(
    async (event: React.SyntheticEvent) => {
      if (!agentInstructions) return
      try {
        await copyText(agentInstructions)
        sendEvent('agent_sessions.task_view', {
          target: 'COPY_SESSION_PROMPT',
          session_id: session.id,
          task_id: session.resource_global_id,
        })
      } catch {
        event.preventDefault()
        event.stopPropagation()
      }
    },
    [agentInstructions, session],
  )

  const sessionName = useMemo(() => {
    if (isPending) {
      return 'New session'
    }

    return session.name
  }, [isPending, session.name])

  const statusIcon = useMemo(() => {
    if (isCancelled) {
      return <SkipIcon className="fgColor-muted" />
    } else if (isCompleted) {
      return <CheckIcon className="fgColor-success" />
    } else if (isFailed) {
      return <XCircleFillIcon className="fgColor-danger" />
    }
    return null
  }, [isCompleted, isCancelled, isFailed])

  const duration = useMemo(() => {
    if (isPending) return null

    if (isActive || isCompleted || isCancelled || isFailed) {
      return (
        <SessionElapsedTime
          createdAt={session.created_at}
          completedAt={session.completed_at}
          updatedAt={session.updated_at ?? session.last_updated_at}
          state={session.state}
        />
      )
    }
    return null
  }, [
    isPending,
    isActive,
    isCompleted,
    isCancelled,
    isFailed,
    session.created_at,
    session.completed_at,
    session.updated_at,
    session.last_updated_at,
    session.state,
  ])

  if (isChatSession) {
    return null
  }

  return (
    <div
      ref={ref}
      data-active={isActive}
      data-expanded={!isCollapsed}
      className={clsx(styles.container, shouldHighlight && styles.highlighted)}
    >
      <div className={styles.agentAvatar}>
        <AgentAvatar
          size={24}
          animationType={isActive || isPending ? 'thinking' : 'static'}
          loopAnimation
          agentId={session.agent_id}
        />
      </div>
      <div className={styles.sessionNameGroup}>
        <span className={clsx(styles.sessionName, 'line-clamp-3')} id="session-header-title">
          {sessionName}
        </span>
        <button
          onClick={handleClick}
          type="button"
          className={styles.toggleButton}
          aria-expanded={!isCollapsed}
          aria-controls={contentId}
          aria-labelledby="session-header-title"
        >
          <ChevronRightIcon className={styles.chevron} data-expanded={!isCollapsed} />
        </button>
      </div>

      {(statusIcon || duration) && (
        <div className={styles.statusDuration}>
          {statusIcon && <span className={styles.statusIcon}>{statusIcon}</span>}
          {duration && <span className="text-small text-medium text-tabular-nums">{duration}</span>}
        </div>
      )}
      <div className={styles.actions}>
        {showMoreActionsMenu && (
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton icon={KebabHorizontalIcon} aria-label="More actions" variant="invisible" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay align="end">
              <ActionList>
                {workflowLink && (
                  <ActionList.LinkItem href={workflowLink} onClick={handleLinkClick}>
                    View verbose logs
                  </ActionList.LinkItem>
                )}
                {sessionLink && <ActionList.Item onSelect={handleCopyLink}>Copy link</ActionList.Item>}
                {agentInstructions && (
                  <ActionList.Item onSelect={handleCopyPrompt}>Copy original prompt</ActionList.Item>
                )}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}
      </div>
    </div>
  )
}
